import React from 'react'

interface InteligenciaM4Props {
  metricas: any
  topSegmentos: any
  insights: any[]
}

const BlocoInteligenciaM4: React.FC<InteligenciaM4Props> = ({ 
  metricas, 
  topSegmentos, 
  insights 
}) => {
  if (!metricas) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
          <span className="text-3xl mr-3">📊</span>
          Inteligência da M4
        </h2>
        <div className="text-center py-8 text-gray-500">
          Sem dados disponíveis para o período selecionado
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        <span className="text-3xl mr-3">📊</span>
        Inteligência da M4
        <span className="ml-3 text-sm font-normal text-gray-500">
          Visão Estratégica da Agência
        </span>
      </h2>

      {/* Cards de Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* ROI Médio Geral */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
          <div className="text-sm text-green-700 font-medium mb-1">ROI Médio Geral</div>
          <div className="text-3xl font-bold text-green-800">
            {metricas.roi_medio_geral?.toFixed(0) || 0}%
          </div>
          <div className="text-xs text-green-600 mt-1">
            Mediana: {metricas.roi_mediana_geral?.toFixed(0) || 0}%
          </div>
        </div>

        {/* Faturamento Total */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
          <div className="text-sm text-blue-700 font-medium mb-1">Faturamento Total</div>
          <div className="text-3xl font-bold text-blue-800">
            R$ {((metricas.faturamento_total || 0) / 1000).toFixed(0)}k
          </div>
          <div className="text-xs text-blue-600 mt-1">
            {metricas.total_clientes_validos || 0} clientes
          </div>
        </div>

        {/* Clientes em Risco */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg p-4 border border-red-200">
          <div className="text-sm text-red-700 font-medium mb-1">Clientes em Risco</div>
          <div className="text-3xl font-bold text-red-800">
            {metricas.percentual_clientes_risco?.toFixed(0) || 0}%
          </div>
          <div className="text-xs text-red-600 mt-1">
            {metricas.qtd_risco_alto || 0} alto | {metricas.qtd_risco_medio || 0} médio
          </div>
        </div>

        {/* Potencial de Receita */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
          <div className="text-sm text-purple-700 font-medium mb-1">Potencial de Receita</div>
          <div className="text-3xl font-bold text-purple-800">
            R$ {((metricas.potencial_receita_total || 0) / 1000).toFixed(0)}k
          </div>
          <div className="text-xs text-purple-600 mt-1">
            {metricas.percentual_oportunidades?.toFixed(0) || 0}% com oportunidades
          </div>
        </div>
      </div>

      {/* Segmentos Destaque */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Segmento Mais Lucrativo */}
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-5 border border-amber-200">
          <div className="flex items-center mb-3">
            <span className="text-2xl mr-2">💰</span>
            <h3 className="text-lg font-bold text-amber-900">Mais Lucrativo</h3>
          </div>
          <div className="text-xl font-bold text-amber-800 mb-1">
            {metricas.segmento_mais_lucrativo || 'N/A'}
          </div>
          <div className="text-sm text-amber-700">
            R$ {((metricas.faturamento_segmento_mais_lucrativo || 0) / 1000).toFixed(0)}k total
          </div>
          <div className="text-xs text-amber-600 mt-1">
            {metricas.qtd_clientes_segmento_mais_lucrativo || 0} clientes
          </div>
        </div>

        {/* Segmento Maior ROI */}
        <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-5 border border-green-200">
          <div className="flex items-center mb-3">
            <span className="text-2xl mr-2">📈</span>
            <h3 className="text-lg font-bold text-green-900">Maior ROI</h3>
          </div>
          <div className="text-xl font-bold text-green-800 mb-1">
            {metricas.segmento_maior_roi || 'N/A'}
          </div>
          <div className="text-sm text-green-700">
            {metricas.roi_segmento_maior_roi?.toFixed(0) || 0}% ROI médio
          </div>
          <div className="text-xs text-green-600 mt-1">
            Oportunidade de prospecção
          </div>
        </div>

        {/* Segmento Maior Crescimento */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-5 border border-blue-200">
          <div className="flex items-center mb-3">
            <span className="text-2xl mr-2">🚀</span>
            <h3 className="text-lg font-bold text-blue-900">Maior Crescimento</h3>
          </div>
          <div className="text-xl font-bold text-blue-800 mb-1">
            {metricas.segmento_maior_crescimento || 'N/A'}
          </div>
          <div className="text-sm text-blue-700">
            {metricas.crescimento_segmento_maior 
              ? `+${metricas.crescimento_segmento_maior.toFixed(0)}% crescimento`
              : 'Sem dados'}
          </div>
          <div className="text-xs text-blue-600 mt-1">
            Tendência positiva
          </div>
        </div>
      </div>

      {/* Top 3 Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Top 3 ROI */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-bold text-gray-800 mb-3 flex items-center">
            <span className="mr-2">🏆</span>
            Top 3 ROI
          </h4>
          <div className="space-y-2">
            {topSegmentos.roi && topSegmentos.roi.length > 0 ? (
              topSegmentos.roi.map((seg: any, idx: number) => (
                <div key={seg.segmento_id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">
                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                    </span>
                    <span className="text-sm font-medium text-gray-700">
                      {seg.segmento_nome}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-green-700">
                    {seg.roi_medio_segmento?.toFixed(0) || 0}%
                  </span>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500 text-center py-2">Sem dados</div>
            )}
          </div>
        </div>

        {/* Top 3 Crescimento */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-bold text-gray-800 mb-3 flex items-center">
            <span className="mr-2">📊</span>
            Top 3 Crescimento
          </h4>
          <div className="space-y-2">
            {topSegmentos.crescimento && topSegmentos.crescimento.length > 0 ? (
              topSegmentos.crescimento.map((seg: any, idx: number) => (
                <div key={seg.segmento_id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">
                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                    </span>
                    <span className="text-sm font-medium text-gray-700">
                      {seg.segmento_nome}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-blue-700">
                    +{seg.crescimento_medio_segmento?.toFixed(0) || 0}%
                  </span>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500 text-center py-2">Sem dados</div>
            )}
          </div>
        </div>

        {/* Top 3 Potencial */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-bold text-gray-800 mb-3 flex items-center">
            <span className="mr-2">💎</span>
            Top 3 Potencial
          </h4>
          <div className="space-y-2">
            {topSegmentos.potencial && topSegmentos.potencial.length > 0 ? (
              topSegmentos.potencial.map((seg: any, idx: number) => (
                <div key={seg.segmento_id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">
                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                    </span>
                    <span className="text-sm font-medium text-gray-700">
                      {seg.segmento_nome}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-purple-700">
                    R$ {((seg.potencial_receita_total || 0) / 1000).toFixed(0)}k
                  </span>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500 text-center py-2">Sem dados</div>
            )}
          </div>
        </div>
      </div>

      {/* Insights Automáticos */}
      {insights && insights.length > 0 && (
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-5 border border-orange-200">
          <h4 className="font-bold text-orange-900 mb-4 flex items-center">
            <span className="mr-2">💡</span>
            Insights Estratégicos
          </h4>
          <div className="space-y-3">
            {insights.map((insight, idx) => (
              <div 
                key={idx} 
                className={`p-3 rounded-lg border ${
                  insight.tipo === 'critico' ? 'bg-red-50 border-red-300' :
                  insight.tipo === 'alerta' ? 'bg-yellow-50 border-yellow-300' :
                  insight.tipo === 'oportunidade' ? 'bg-purple-50 border-purple-300' :
                  'bg-green-50 border-green-300'
                }`}
              >
                <div className="font-bold text-sm mb-1">
                  {insight.titulo}
                </div>
                <div className="text-xs text-gray-700 mb-2">
                  {insight.descricao}
                </div>
                {insight.acao && (
                  <div className="text-xs font-medium text-gray-800">
                    → {insight.acao}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default BlocoInteligenciaM4
